/**
 * 跨标签页登录身份同步。
 *
 * localStorage 是多标签页共享的，但每个标签页的 Vuex 状态、已渲染的菜单以及页面上缓存的
 * 机构 / 权限数据都是各自独立的。用户在新标签页换账号或换机构重新登录后，老标签页会用
 * 「新 token + 旧身份参数」继续请求，后端多半返回 403，前端再按登录失效把用户赶回登录页
 * —— 用户看到的就是「刚登录完又让我登录一次」。
 *
 * 本模块借 storage 事件感知别的标签页把登录身份换掉，提示用户刷新本页以对齐新身份。只在
 * 「身份（账号 + 机构）真的变了」时提示：token 轮换、菜单更新等同账号内的变化不打扰用户；
 * 登录态被清除（退出登录）不在本模块处理范围，交给既有的失效码与 WorkBuddy 恢复逻辑。
 *
 * @module utils/authSessionSync
 */
import { ElMessageBox } from 'element-plus';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { getStorageAuthInfo, checkLogin } from '@/utils/storageOperateApi';
import { isAuthTransitioning } from '@/utils/authTransition';
import { isWorkBuddySessionRecovering } from '@/utils/workBuddySessionRecovery';
import i18n from '@/plugins/i18n';
import slsLogger from '@/utils/slsLogger';

/** slsLogger 组件名 */
const LOG_COMPONENT = 'AuthSessionSync';

/** 本页已知的登录身份，用于比对别的标签页写入的新身份 */
let knownIdentity = '';
/** 是否已弹过刷新提示，避免连续的 storage 事件重复弹框 */
let identityChangedNotified = false;

/**
 * 读取当前本地登录身份指纹：账号 + 所属机构。
 * 只取身份维度，token 轮换不参与比对。
 * @returns {string} 身份指纹，未登录时为空串
 */
function readIdentity() {
  const jmAuthInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
  const { brmToken } = getStorageAuthInfo() || {};
  const uid = jmAuthInfo.uid || brmToken?.userId || '';
  const orgId = jmAuthInfo.outsideOrgId || jmAuthInfo.organizationId || brmToken?.goId || '';
  if (!uid && !orgId) return '';
  return `${uid}@${orgId}`;
}

/**
 * 提示用户刷新本页以对齐新的登录身份。
 * @param {string} nextIdentity - 别的标签页写入的新身份指纹
 * @returns {void}
 */
function notifyIdentityChanged(nextIdentity) {
  identityChangedNotified = true;
  slsLogger.console({
    component: LOG_COMPONENT,
    message: '跨标签页同步-检测到登录身份被其它标签页更换',
    extra: { knownIdentity, nextIdentity, path: window.location.pathname }
  });
  ElMessageBox.alert(i18n.global.t('common.tip.account_switched_in_other_tab'), i18n.global.t('common.tip.tips'), {
    customClass: 'w480 message-box-normal',
    confirmButtonText: i18n.global.t('common.button.confirm'),
    callback: () => window.location.reload()
  });
}

/**
 * 判断本次 storage 变更是否需要参与身份比对。
 * @param {StorageEvent} event - 其它窗口写入 storage 触发的事件
 * @returns {boolean} 需要比对返回 true
 */
function shouldCheckIdentity(event) {
  const key = event?.key;
  // storage.clear() 会以 key = null 触发一次，同样需要重新比对
  if (key && ![LOCAL_STORAGE_KEY.JM_AUTH_INFO, LOCAL_STORAGE_KEY.BRM_USERINFO].includes(key)) return false;
  // 已提示过，等用户刷新即可
  if (identityChangedNotified) return false;
  // 本机正在换发鉴权，storage 处于中间态，等换发结束后的最终写入再比对
  if (isAuthTransitioning()) return false;
  // WorkBuddy 静默恢复同样会重写鉴权，其中间态不应触发提示
  if (isWorkBuddySessionRecovering()) return false;
  return true;
}

/**
 * 处理其它标签页写入 storage 的变更，识别出登录身份被更换后提示刷新。
 * @param {StorageEvent} event - 其它窗口写入 storage 触发的事件
 * @returns {void}
 */
function handleStorageChange(event) {
  if (!shouldCheckIdentity(event)) return;

  const nextIdentity = readIdentity();
  // 新身份为空或登录态不完整（对方退出登录 / 仍在换发中）不由本模块处理
  if (!nextIdentity || !checkLogin()) return;
  // 本页从未持有登录身份（例如一直停在登录页），不该被别处的登录打断
  if (!knownIdentity) {
    knownIdentity = nextIdentity;
    return;
  }
  // 同一账号同一机构，仅 token 或菜单变化，无需打扰用户
  if (nextIdentity === knownIdentity) return;

  notifyIdentityChanged(nextIdentity);
}

/**
 * 安装跨标签页登录身份同步，需在应用启动时调用一次。
 * @param {import('vue-router').Router} router - 应用路由实例
 * @returns {void}
 * @example
 * installAuthSessionSync(router);
 */
export function installAuthSessionSync(router) {
  knownIdentity = readIdentity();

  // 本页自己登录成功不会触发 storage 事件，借导航补记当前身份
  router.afterEach(() => {
    if (identityChangedNotified) return;
    const identity = readIdentity();
    if (identity) knownIdentity = identity;
  });

  // storage 事件只在其它窗口修改时触发，正好用来感知别处的重新登录
  window.addEventListener('storage', handleStorageChange);
}
